import { OvFormInput } from '@components'
import { TOOLTIPS, EMAIL_PROVIDERS, LABELS } from '@constants'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { Add, Delete, Edit, History, CheckCircle, Error as ErrorIcon, Warning, AccessTime } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { EmailTemplateForm, EmailLogsDialog } from '../email'
import { useFormContext, useWatch } from 'react-hook-form'

/**
 * Display email configuration test status with visual indicator
 * Shows status for the currently selected provider (SMTP only)
 */
const EmailConfigStatus = observer(({ provider }: { provider: string }) => {
  const { company } = useStore()
  const currentCompany = company.current_company

  // Only show for SMTP provider (Resend doesn't need configuration testing)
  if (provider !== EMAIL_PROVIDERS.SMTP) {
    return null
  }

  // Only show if we have test results
  if (!currentCompany?.email_config_test_status) {
    return null
  }

  const status = currentCompany.email_config_test_status
  const message = currentCompany.email_config_test_message
  const testedAt = currentCompany.email_config_last_tested_at

  const getStatusColor = () => {
    if (status === 'success') return 'success'
    if (status === 'failed') return 'error'
    return 'warning'
  }

  const getStatusIcon = () => {
    if (status === 'success') return <CheckCircle fontSize="small" />
    if (status === 'failed') return <ErrorIcon fontSize="small" />
    return <AccessTime fontSize="small" />
  }

  const getStatusText = () => {
    if (status === 'success') return 'E-mail service werkt correct'
    if (status === 'failed') return 'E-mail service werkt niet'
    return 'E-mail service wordt getest'
  }

  const getTimeAgo = (dateString?: string) => {
    if (!dateString) return ''

    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Zojuist getest'
      if (diffMins < 60) return `${diffMins} minuten geleden getest`
      if (diffHours < 24) return `${diffHours} uur geleden getest`
      return `${diffDays} dagen geleden getest`
    } catch {
      return ''
    }
  }

  return (
    <Alert
      severity={getStatusColor()}
      icon={getStatusIcon()}
      sx={{ mt: 2 }}>
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {getStatusText()}
        </Typography>
        {message && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {message}
          </Typography>
        )}
        {testedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {getTimeAgo(testedAt)}
          </Typography>
        )}
      </Box>
    </Alert>
  )
})

const SmtpTestButton = observer(() => {
  const { company } = useStore()
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const { control } = useFormContext()

  const smtpHost = useWatch({ control, name: 'company.smtp_host' })
  const smtpPort = useWatch({ control, name: 'company.smtp_port' })
  const smtpUsername = useWatch({ control, name: 'company.smtp_username' })
  const smtpPassword = useWatch({ control, name: 'company.smtp_password' })
  const smtpEncryption = useWatch({ control, name: 'company.smtp_encryption' })

  const handleTestConnection = async () => {
    setTesting(true)
    setTestResult(null)

    try {
      let result

      // If password is empty but SMTP is configured, use saved configuration
      if (!smtpPassword && company.current_company?.has_custom_smtp) {
        result = await company.testSmtpConnection({
          use_saved: true
        } as any)
      } else {
        // Use form values for testing new configuration
        if (!smtpHost || !smtpPort || !smtpUsername || !smtpPassword) {
          setTestResult({
            success: false,
            message: 'Vul alle SMTP velden in voordat je de verbinding test'
          })
          setTesting(false)
          return
        }

        result = await company.testSmtpConnection({
          smtp_host: smtpHost,
          smtp_port: parseInt(smtpPort),
          smtp_username: smtpUsername,
          smtp_password: smtpPassword,
          smtp_encryption: smtpEncryption
        })
      }

      setTestResult(result)
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error?.message || 'Verbinding mislukt'
      })
    } finally {
      setTesting(false)
    }
  }

  // Button is enabled if we have either form values OR saved backend values
  const hasFormValues = smtpHost && smtpPort && smtpUsername && smtpPassword
  const hasSavedConfig = company.current_company?.has_custom_smtp
  const isTestButtonDisabled = testing || (!hasFormValues && !hasSavedConfig)

  return (
    <Box>
      <Tooltip title={TOOLTIPS.TEST_SMTP_CONNECTION}>
        <span>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={isTestButtonDisabled}
            startIcon={testing ? <CircularProgress size={20} /> : undefined}>
            {testing ? 'Testen...' : 'Test Verbinding'}
          </Button>
        </span>
      </Tooltip>
      {testResult && (
        <Alert
          severity={testResult.success ? 'success' : 'error'}
          icon={testResult.success ? <CheckCircle /> : <ErrorIcon />}
          sx={{ mt: 2 }}>
          {testResult.message}
        </Alert>
      )}
    </Box>
  )
})

export const EmailSettingsForm = observer(() => {
  const FormInput = OvFormInput<any>
  const { email, company } = useStore()
  const isMobile = useIsMobile()
  const [formOpen, setFormOpen] = useState(false)
  const [logsDialogOpen, setLogsDialogOpen] = useState(false)
  const [editingTemplateId, setEditingTemplateId] = useState<number | undefined>(undefined)
  const { control, setValue } = useFormContext()

  const selectedProvider = useWatch({ control, name: 'company.email_provider' })

  // Resend is now the default platform sender
  // Legacy OAuth providers (Gmail/Microsoft) are deprecated but kept for backward compatibility
  const isResendSelected = selectedProvider === EMAIL_PROVIDERS.RESEND ||
    (selectedProvider === null || selectedProvider === undefined || selectedProvider === '')
  const isSmtpSelected = selectedProvider === EMAIL_PROVIDERS.SMTP

  useEffect(() => {
    email.getTemplates()
    return () => email.reset()
  }, [])

  const handleCreate = () => {
    setEditingTemplateId(undefined)
    setFormOpen(true)
  }

  const handleEdit = (id: number) => {
    setEditingTemplateId(id)
    setFormOpen(true)
  }

  const handleDelete = async (id: number) => {
    if (window.confirm('Weet je zeker dat je dit sjabloon wilt verwijderen?')) {
      try {
        await email.deleteTemplate(id)
      } catch (error) {
        console.error('Error deleting template:', error)
      }
    }
  }

  const handleFormClose = () => {
    setFormOpen(false)
    setEditingTemplateId(undefined)
  }

  const handleFormSuccess = async () => {
    await email.getTemplates()
  }

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      invoice: 'Factuur',
      quotation: 'Offerte',
      reminder: 'Herinnering',
      general: 'Algemeen',
    }
    return labels[type] || type
  }

  const getTypeColor = (type: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'warning' | 'default'> = {
      invoice: 'primary',
      quotation: 'secondary',
      reminder: 'warning',
      general: 'default',
    }
    return colors[type] || 'default'
  }

  return (
    <Stack spacing={4}>
      {/* Email Provider Selection */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Kies je e-mail provider
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Selecteer hoe je facturen en offertes wilt versturen
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {/* Resend Platform Email Card (Default) */}
          <Card
            variant="outlined"
            sx={{
              flex: 1,
              cursor: 'pointer',
              border: isResendSelected ? 2 : 1,
              borderColor: isResendSelected ? 'primary.main' : 'divider',
              bgcolor: isResendSelected ? 'action.selected' : 'background.paper',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => setValue('company.email_provider', EMAIL_PROVIDERS.RESEND, { shouldDirty: true })}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: isResendSelected ? 'primary.main' : 'action.selected',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  {isResendSelected ? (
                    <CheckCircle sx={{ color: 'white' }} />
                  ) : (
                    <Typography variant="h6" color="text.secondary">
                      ✉
                    </Typography>
                  )}
                </Box>
                <Box flex={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {isResendSelected && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                        }}
                      />
                    )}
                    <Typography variant="h6">
                      Outvoice Platform Email
                    </Typography>
                    {isResendSelected && (
                      <Typography variant="caption" color="success.main" sx={{ ml: 0.5 }}>
                        {LABELS.ACTIVE}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Verstuur via Outvoice platform - eenvoudig en betrouwbaar
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* SMTP Option Card */}
          <Card
            variant="outlined"
            sx={{
              flex: 1,
              cursor: 'pointer',
              border: isSmtpSelected ? 2 : 1,
              borderColor: isSmtpSelected ? 'primary.main' : 'divider',
              bgcolor: isSmtpSelected ? 'action.selected' : 'background.paper',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => setValue('company.email_provider', EMAIL_PROVIDERS.SMTP, { shouldDirty: true })}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: isSmtpSelected ? 'primary.main' : 'action.selected',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  {isSmtpSelected ? (
                    <CheckCircle sx={{ color: 'white' }} />
                  ) : (
                    <Typography variant="h6" color="text.secondary">
                      @
                    </Typography>
                  )}
                </Box>
                <Box flex={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {isSmtpSelected && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                        }}
                      />
                    )}
                    <Typography variant="h6">
                      SMTP
                    </Typography>
                    {isSmtpSelected && (
                      <Typography variant="caption" color="success.main" sx={{ ml: 0.5 }}>
                        {LABELS.ACTIVE}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Gebruik je eigen e-mailserver
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        {/* Resend Info Alert */}
        {isResendSelected && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Outvoice Platform Email
            </Typography>
            <Typography variant="body2">
              E-mails worden verzonden via <strong>klantfacturen@outvoice.nl</strong> met jouw naam in de afzender.
              Ontvangers kunnen direct op jouw eigen e-mailadres reageren.
            </Typography>
          </Alert>
        )}

        {/* Email Configuration Status - Shows for SMTP only */}
        <EmailConfigStatus provider={selectedProvider || EMAIL_PROVIDERS.RESEND} />
      </Box>

      {/* SMTP Configuration Section */}
      {isSmtpSelected && (
        <>
          <Box>
            <Typography variant="h6" gutterBottom>
              SMTP Configuratie
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Vul de gegevens van je e-mailserver in
            </Typography>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="column" gap={2}>
                  <FormInput
                    label="SMTP Host"
                    name="company.smtp_host"
                    helperText="Bijv. smtp.gmail.com of smtp.office365.com"
                  />
                  <FormInput
                    label="SMTP Poort"
                    name="company.smtp_port"
                    type="number"
                    inputProps={{
                      type: 'number',
                      min: 1,
                      max: 65535,
                    }}
                    helperText="Meestal 587 (TLS) of 465 (SSL)"
                  />
                  <FormInput
                    label="SMTP Gebruikersnaam"
                    name="company.smtp_username"
                    helperText="Je volledige e-mailadres"
                  />
                  <FormInput
                    label="SMTP Wachtwoord"
                    name="company.smtp_password"
                    type="password"
                    helperText="Het wachtwoord van je e-mailaccount"
                  />
                  <FormInput
                    label="Versleuteling"
                    name="company.smtp_encryption"
                    type="select"
                    options={[
                      { value: '', label: 'Geen' },
                      { value: 'tls', label: 'TLS' },
                      { value: 'ssl', label: 'SSL' },
                    ]}
                    valueKey="value"
                    labelKey="label"
                    output="value"
                    helperText="De versleutelingsmethode (meestal TLS)"
                  />
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Optionele instellingen
                  </Typography>
                  <FormInput
                    label="Afzender E-mailadres"
                    name="company.smtp_from_address"
                    helperText={
                      'Het e-mailadres dat als afzender wordt getoond ' +
                      '(optioneel, gebruikt smtp_username als niet ingevuld)'
                    }
                  />
                  <FormInput
                    label="Afzender Naam"
                    name="company.smtp_from_name"
                    helperText="De naam die als afzender wordt getoond (optioneel)"
                  />
                  <Divider sx={{ my: 1 }} />
                  <SmtpTestButton />
                </Stack>
              </CardContent>
            </Card>
          </Box>
          <Divider />
        </>
      )}

      {/* Email Templates Section */}
      <Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
          mb={2}>
          <Typography variant="h6">E-mailsjablonen</Typography>
          <Tooltip title={TOOLTIPS.CREATE_EMAIL_TEMPLATE}>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{ width: { xs: '100%', sm: 'auto' } }}>
              Nieuw sjabloon
            </Button>
          </Tooltip>
        </Stack>

        {!company.current_company?.has_custom_smtp && isSmtpSelected && (
          <Alert
            severity="warning"
            icon={<Warning />}
            sx={{ mb: 2 }}>
            Configureer eerst je SMTP server om e-mails te kunnen versturen via je eigen e-mailserver.
          </Alert>
        )}
        {isResendSelected && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}>
            Outvoice Platform Email is actief. Je kunt direct facturen en offertes versturen.
          </Alert>
        )}

        <Card variant="outlined">
          <CardContent>
            {email.loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography>Laden...</Typography>
              </Box>
            ) : email.templates_list.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">Geen e-mailsjablonen gevonden</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}>
                  Klik op &apos;Nieuw sjabloon&apos; om een e-mailsjabloon aan te maken
                </Typography>
              </Box>
            ) : isMobile ? (
              /* Mobile: Card layout */
              <Stack spacing={2}>
                {email.templates_list.map(template => (
                  <Card key={template.id}>
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                          }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600 }}>
                              {template.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Chip
                                label={getTypeLabel(template.type)}
                                color={getTypeColor(template.type)}
                                size="small"
                              />
                              {template.is_default && (
                                <Chip
                                  label="Standaard"
                                  color="success"
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Bewerken">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEdit(template.id)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Verwijderen">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(template.id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary">
                          {template.subject}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              /* Desktop: Table layout */
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Naam</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Onderwerp</TableCell>
                      <TableCell>Standaard</TableCell>
                      <TableCell align="right">Acties</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {email.templates_list.map(template => (
                      <TableRow
                        key={template.id}
                        hover>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="medium">
                            {template.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getTypeLabel(template.type)}
                            color={getTypeColor(template.type)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary">
                            {template.subject}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {template.is_default && (
                            <Chip
                              label="Standaard"
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end">
                            <Tooltip title={TOOLTIPS.EDIT_EMAIL_TEMPLATE}>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(template.id)}
                                title="Bewerken">
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={TOOLTIPS.DELETE_EMAIL_TEMPLATE}>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(template.id)}
                                title="Verwijderen"
                                color="error">
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <EmailTemplateForm
          open={formOpen}
          onClose={handleFormClose}
          templateId={editingTemplateId}
          onSuccess={handleFormSuccess}
        />
      </Box>

      <Divider />

      {/* Email Logs Section */}
      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}>
          <Box>
            <Typography variant="h6">E-mail Logboek</Typography>
            <Typography
              variant="body2"
              color="text.secondary">
              Bekijk de geschiedenis van verzonden e-mails
            </Typography>
          </Box>
          <Tooltip title={TOOLTIPS.VIEW_EMAIL_LOGS}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<History />}
              onClick={() => setLogsDialogOpen(true)}>
              Toon logboek
            </Button>
          </Tooltip>
        </Stack>

        <EmailLogsDialog
          open={logsDialogOpen}
          onClose={() => setLogsDialogOpen(false)}
        />
      </Box>
    </Stack>
  )
})
